import { type UseFormReturn } from "react-hook-form"
import { type IOffer } from "@commonTypes/offer"
import type { IOrder, IOrderPayload } from "@commonTypes/order"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Input } from "@components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@components/ui/input-group"
import { Badge } from "@components/ui/badge"
import { Checkbox } from "@components/ui/checkbox"
import {
  useGetStatesQuery,
  useGetRevenueClassesQuery,
} from "@apis/shared/useShared"
import { useGetSalesLocationsQuery } from "@apis/salesLocations/useSalesLocations"
import { useGetAlternateOffersQuery } from "@apis/orders/useOrders"
import { useHasPermissions } from "@hooks/useHasPermissions"
import { Alert, AlertDescription, AlertTitle } from "@components/ui/alert"
import { Tooltip, TooltipTrigger, TooltipContent } from "@components/ui/tooltip"
import { AlertCircleIcon } from "lucide-react"
import { isDuplicateAccountError } from "@apis/orders/utils"
import { cn } from "@components/ui/lib/utils"

type EditOrderFormProps = {
  form: UseFormReturn<IOrderPayload & { market_code: string }>
  order_data: IOrder
  offer?: IOffer
}

const getOverrideAlertsVisibility = ({
  hasPermission,
  duplicateAccountsCount,
}: {
  hasPermission: boolean
  duplicateAccountsCount: number
}) => ({
  showSkip: hasPermission,
  showMerge: hasPermission && duplicateAccountsCount > 0,
})

export const EditOrderForm = ({
  form,
  order_data,
  offer,
}: EditOrderFormProps) => {
  const { canOverrideDuplicateAccountValidation } = useHasPermissions()
  const { order_payload } = order_data

  const duplicateAccounts =
    order_data.error_messages?.find(isDuplicateAccountError)?.meta
      ?.duplicate_accounts ?? []

  const { showSkip: showSkipAlert, showMerge: showMergeAlert } =
    getOverrideAlertsVisibility({
      hasPermission: canOverrideDuplicateAccountValidation,
      duplicateAccountsCount: duplicateAccounts.length,
    })

  const skipChecked = form.watch("skip_duplicate_account_validation") === true
  const mergeChecked = form.watch("merge_into_existing_account") === true

  const { data: alternateOffers = [] } = useGetAlternateOffersQuery(
    order_payload.order_id as number,
  )
  const { data: states = [] } = useGetStatesQuery()
  const { data: revenueClasses = [] } = useGetRevenueClassesQuery()
  const { data: salesLocations = [] } = useGetSalesLocationsQuery({
    active: false,
  })

  const allOffers = [
    ...(offer && !alternateOffers.some((o) => o.offer_code === offer.offer_code)
      ? [offer]
      : []),
    ...alternateOffers,
  ]

  const offerOptions = allOffers.map((offer) => ({
    value: offer.offer_code,
    label: `${offer.market_code} (${offer.offer_desc})`,
    offer_code: offer.offer_code,
  }))

  const salesLocationsOptions = salesLocations.map((salesLocation) => ({
    value: salesLocation.location_code,
    label: salesLocation.location_name,
  }))

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="tw:grid tw:grid-cols-12 tw:items-baseline tw:gap-4"
      >
        <div className="tw:col-span-12">
          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:font-medium">
            <FormLabel htmlFor="sale_dt">Sale Date</FormLabel>
            <p id="sale_dt" className="mb-0">
              {order_payload.sale_dt}
            </p>
          </div>
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="email"
            rules={{ required: "Email is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Enter email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="primary_phone"
            rules={{
              required: "Primary phone is required",
              pattern: {
                value: /^\d{10}$/,
                message: "Phone must be a 10-digit number",
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Primary Phone</FormLabel>
                <FormControl>
                  <Input placeholder="1234567890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="first_name"
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="last_name"
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <hr className="tw:col-span-12" />

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="service_address_line_1"
            rules={{ required: "Service address is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Line 1</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="service_address_line_2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Line 2</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-4">
          <FormField
            control={form.control}
            name="service_city"
            rules={{ required: "Service city is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service City</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {states.length > 0 && (
          <div className="tw:col-span-12 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="service_state"
              rules={{ required: "Service state is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service State</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={typeof field.value === "string" ? field.value : ""}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="tw:col-span-12 tw:md:col-span-4">
          <FormField
            control={form.control}
            name="service_zip_code"
            rules={{ required: "Service ZIP code is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service ZIP</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <hr className="tw:col-span-12" />

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="billing_address_line_1"
            rules={{ required: "Billing address is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing Line 1</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="billing_address_line_2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing Line 2</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-4">
          <FormField
            control={form.control}
            name="billing_city"
            rules={{ required: "Billing city is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing City</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {states.length > 0 && (
          <div className="tw:col-span-12 tw:md:col-span-4">
            <FormField
              control={form.control}
              name="billing_state"
              rules={{ required: "Billing state is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing State</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {states.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="tw:col-span-12 tw:md:col-span-4">
          <FormField
            control={form.control}
            name="billing_zip_code"
            rules={{ required: "Billing ZIP code is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing ZIP</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <hr className="tw:col-span-12" />

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="account_number"
            rules={{
              pattern: offer?.validation_rules?.account_number_regexp
                ? {
                    value: new RegExp(
                      offer.validation_rules.account_number_regexp,
                    ),
                    message: "Invalid account number format",
                  }
                : undefined,
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Number</FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput {...field} />
                    <InputGroupAddon align="inline-end">
                      <Badge variant="secondary">
                        {offer?.validation_rules.account_number_example}
                      </Badge>
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6 tw:md:self-center">
          <FormField
            control={form.control}
            name="has_account_number"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="tw:flex tw:items-center tw:gap-3">
                    <FormLabel>Has Account Number?</FormLabel>
                    <Checkbox
                      id="has_account_number"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="tw:col-span-12 tw:md:col-span-6">
          <FormField
            control={form.control}
            name="offer_code"
            rules={{ required: "Offer Code is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Offer Code</FormLabel>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput {...field} readOnly />
                    <InputGroupAddon align="inline-end">
                      <Badge variant="secondary">${offer?.rate}</Badge>
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {offerOptions.length > 0 && (
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="offer_code"
              rules={{ required: "Offer Code is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Market</FormLabel>
                  <Select
                    onValueChange={(selectedMarketCode) => {
                      field.onChange(selectedMarketCode)

                      const selected = offerOptions.find(
                        (o) => o.value === selectedMarketCode,
                      )
                      if (selected?.offer_code) {
                        form.setValue("offer_code", selected.offer_code)
                      }
                    }}
                    value={field.value || ""}
                  >
                    <FormControl className="tw:w-full tw:overflow-hidden">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Market" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {offerOptions.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {revenueClasses.length > 0 && (
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="revenue_class_code"
              rules={{
                validate: (val) => val !== "" || "Revenue Class is required",
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Revenue Class</FormLabel>
                  <Select
                    onValueChange={(val) =>
                      field.onChange(val === "__none__" ? "" : val)
                    }
                    value={field.value || "__none__"}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Revenue Class" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem key="None" value="__none__">
                        None
                      </SelectItem>
                      {revenueClasses.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {salesLocationsOptions.length > 0 && (
          <div className="tw:col-span-12 tw:md:col-span-6">
            <FormField
              control={form.control}
              name="sales_location_code"
              rules={{
                validate: (val) => val !== "" || "Sales Location is required",
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sales Location</FormLabel>
                  <Select
                    onValueChange={(val) =>
                      field.onChange(val === "__none__" ? "" : val)
                    }
                    value={field.value || "__none__"}
                  >
                    <FormControl className="tw:w-full">
                      <SelectTrigger>
                        <SelectValue placeholder="Select Location" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem key="None" value="__none__">
                        None
                      </SelectItem>
                      {salesLocationsOptions.map(
                        (option: { value: string; label: string }) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="tw:col-span-12 tw:grid tw:grid-cols-subgrid tw:gap-4">
          {showSkipAlert && (
            <div
              className={cn("tw:col-span-12 tw:md:col-span-4", {
                "tw:md:col-start-5": showMergeAlert,
                "tw:md:col-start-9": !showMergeAlert,
              })}
            >
              <Alert
                variant="destructive"
                className={cn("tw:gap-y-2", { "tw:opacity-60": mergeChecked })}
              >
                <AlertCircleIcon />
                <AlertTitle>
                  <FormField
                    control={form.control}
                    name="skip_duplicate_account_validation"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="tw:flex tw:items-center tw:gap-3">
                                <Checkbox
                                  id="skip_duplicate_account_validation"
                                  checked={field.value ?? false}
                                  onCheckedChange={field.onChange}
                                  disabled={mergeChecked}
                                />
                                <FormLabel
                                  htmlFor="skip_duplicate_account_validation"
                                  className="tw:cursor-pointer tw:font-medium"
                                >
                                  Skip duplicate account check
                                </FormLabel>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {mergeChecked
                                ? "Cannot be used together with Merge into existing account."
                                : "Use only when the duplicate is intentional."}
                            </TooltipContent>
                          </Tooltip>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AlertTitle>
              </Alert>
            </div>
          )}

          {showMergeAlert && (
            <div className="tw:col-span-12 tw:md:col-span-4 tw:md:col-start-9">
              <Alert
                variant="destructive"
                className={cn("tw:gap-y-2", { "tw:opacity-60": skipChecked })}
              >
                <AlertCircleIcon />
                <AlertTitle>
                  <FormField
                    control={form.control}
                    name="merge_into_existing_account"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="tw:flex tw:items-center tw:gap-3">
                                <Checkbox
                                  id="merge_into_existing_account"
                                  checked={field.value ?? false}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked)
                                    if (!checked) {
                                      form.setValue("account_id", undefined)
                                    }
                                  }}
                                  disabled={skipChecked}
                                />
                                <FormLabel
                                  htmlFor="merge_into_existing_account"
                                  className="tw:cursor-pointer tw:font-medium"
                                >
                                  Merge into existing account
                                </FormLabel>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {skipChecked
                                ? "Cannot be used together with Skip duplicate account check."
                                : "The selected account's number will be replaced with the order's account number."}
                            </TooltipContent>
                          </Tooltip>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AlertTitle>
                <AlertDescription>
                  <FormField
                    control={form.control}
                    name="account_id"
                    rules={{
                      validate: (val) =>
                        !mergeChecked ||
                        val !== undefined ||
                        "Select an account to merge into",
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          onValueChange={(val) =>
                            field.onChange(val ? Number(val) : undefined)
                          }
                          value={field.value ? String(field.value) : ""}
                          disabled={skipChecked || !mergeChecked}
                        >
                          <FormControl className="tw:w-full">
                            <SelectTrigger>
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {duplicateAccounts.map((account) => (
                              <SelectItem
                                key={account.account_id}
                                value={String(account.account_id)}
                              >
                                {`Account ID: ${account.account_id}${account.status_group ? ` (${account.status_group})` : ""}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </form>
    </Form>
  )
}
